import React, { useCallback } from 'react';

import {
  ArrayInput,
  AutocompleteInput,
  Form,
  ReferenceInput,
  required,
  SimpleFormIterator,
  useNotify,
  useRecordContext,
  useRefresh,
  useSimpleFormIterator,
  useSimpleFormIteratorItem,
  useUpdate,
} from 'react-admin';

import { FieldValues } from 'react-hook-form';
import { isNil } from 'lodash';

import { Add, RemoveCircleOutline } from '@mui/icons-material';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import useDialogStatus from '../../hooks/useDialogStatus';
import authenticatorTypes from '../../constants/authenticatorTypes';
import errorNotification from '../../utilities/errorNotification';
import resourceNames from '../../constants/resourceNames';

const CustomAddMemberButton = () => {
  const { add } = useSimpleFormIterator();
  return (
    <Button
      startIcon={<Add />}
      size="small"
      variant="text"
      onClick={() => add()}
    >
      Add member
    </Button>
  );
};

const CustomRemoveMemberButton = () => {
  const { remove } = useSimpleFormIterator();
  const { index } = useSimpleFormIteratorItem();
  return (
    <IconButton onClick={() => remove(index)}>
      <RemoveCircleOutline />
    </IconButton>
  );
};

const AssignMemberPermission = () => {
  const { open, openDialog, closeDialog } = useDialogStatus();
  const record = useRecordContext();
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();

  const onClickAssignMembers = useCallback((data: FieldValues) => {
    if (!record) return;
    update(
      resourceNames.PERMISSION_ROLES,
      {
        id: `${record.id}/attach`,
        data: {
          authenticators: (data.authenticators as { id: string }[])
            .filter(({ id }) => !isNil(id))
            .map(({ id }) => ({ id, type: authenticatorTypes.MEMBER })),
        },
      },
      {
        onSuccess: () => {
          notify('Member roles added', { type: 'info' });
          closeDialog();
          refresh();
        },
        onError: (e) => {
          notify(errorNotification(e), { type: 'error' });
        },
      },
    );
  }, [closeDialog, notify, record, refresh, update]);

  if (!record) return null;

  return (
    <>
      <Dialog open={open}>
        <Form onSubmit={onClickAssignMembers}>
          <DialogTitle fontWeight="bold">Assign member</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={6}>
              <Box display="flex" gap={1}>
                <Typography>{'Select the member you want to assign '}</Typography>
                <Typography fontWeight="bold">
                  {record.name}
                </Typography>
                <Typography>role to.</Typography>
              </Box>
              <ArrayInput source="authenticators" label={false} defaultValue={[{}]}>
                <SimpleFormIterator
                  inline
                  sx={{ '.RaSimpleFormIterator-form': { marginY: 2 } }}
                  disableReordering
                  addButton={<CustomAddMemberButton />}
                  removeButton={<CustomRemoveMemberButton />}
                  disableClear
                >
                  <ReferenceInput source="id" reference={resourceNames.MEMBERS} filter={{ active: true }}>
                    <AutocompleteInput
                      label="Member"
                      fullWidth
                      filterSelectedOptions={false}
                      filterToQuery={(searchText) => ({ name: searchText })}
                      optionText={(item: {firstName: string; lastName: string}) => `${item.firstName} ${item.lastName}`}
                      validate={[required()]}
                    />
                  </ReferenceInput>
                </SimpleFormIterator>
              </ArrayInput>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button type="submit" variant="contained">Confirm</Button>
          </DialogActions>
        </Form>

      </Dialog>
      <Button
        startIcon={<Add />}
        size="small"
        variant="contained"
        onClick={openDialog}
      >
        Assign Member
      </Button>
    </>
  );
};

export default AssignMemberPermission;
